// This file will manage the DOM-related logic and effects.
import { useEffect } from 'react'

import { setCaretPosition } from '../utils/domUtils'
import { findSegmentFromCursor } from '../utils/selectionUtils'

import { UseEditorDOMProps } from './types'

export const useEditorDOM = ({ state, refs }: UseEditorDOMProps) => {
  const {
    segments,
    activeIndex,
    focusRequestCounter,
    isNavigating,
    globalCursorPos,
  } = state
  const {
    editorRef,
    elRefs,
    nextCursorPos,
    isSelectionProgrammatic,
    selectionRequestRef,
  } = refs

  useEffect(() => {
    // This effect runs after any render where navigation state might have changed.
    // It handles both programmatic selection and caret positioning.

    // Priority 1: Handle programmatic selection requests.
    if (selectionRequestRef.current?.type === 'all' && editorRef.current) {
      const firstSegment = segments[0]
      const lastSegment = segments[segments.length - 1]
      const firstEl = firstSegment && elRefs.current.get(firstSegment.id)
      const lastEl = lastSegment && elRefs.current.get(lastSegment.id)

      if (firstEl && lastEl) {
        const selection = window.getSelection()
        const range = document.createRange()
        range.setStart(firstEl.firstChild || firstEl, 0)
        range.setEnd(
          lastEl.firstChild || lastEl,
          lastEl.textContent?.length || 0,
        )
        selection?.removeAllRanges()
        selection?.addRange(range)
      }
      // Reset the request after handling it.
      selectionRequestRef.current = null
      return // Stop further processing in this effect.
    }

    // Priority 2: Handle caret positioning in NAVIGATION mode.
    if (isNavigating) {
      const result = findSegmentFromCursor(globalCursorPos, segments)
      if (result) {
        const { segment, localPos } = result
        const element = elRefs.current.get(segment.id)
        if (element) {
          isSelectionProgrammatic.current = true
          setCaretPosition(element, localPos)
        }
      }
      return
    }

    // Priority 3: Handle caret positioning in EDIT mode.
    const activeSegment = segments[activeIndex]
    if (!activeSegment) {
      editorRef.current?.focus()
      return
    }

    const activeEl = elRefs.current.get(activeSegment.id)

    if (activeEl && nextCursorPos.current !== null) {
      activeEl.focus()
      setCaretPosition(activeEl, nextCursorPos.current)
      nextCursorPos.current = null // Reset the cursor position after use
    } else if (activeEl && document.activeElement !== activeEl) {
      activeEl.focus()
    }
  }, [
    isNavigating,
    globalCursorPos,
    segments,
    activeIndex,
    elRefs,
    isSelectionProgrammatic,
    editorRef,
    focusRequestCounter,
    nextCursorPos,
    selectionRequestRef,
  ])

  // NOTE: The separate useEffects have been consolidated into one
  // to manage priority and prevent conflicting DOM updates.
} 